/**
 * Token definitions for the Math Exercise DSL
 *
 * Token order matters in Chevrotain - more specific tokens must come before
 * less specific ones (e.g., keywords before identifiers).
 */
import { createToken, Lexer, TokenType } from 'chevrotain';

// ═══════════════════════════════════════════════════════════════════════════
// Whitespace & Comments
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Newline is SIGNIFICANT in our DSL - it separates lines!
 * Do NOT skip it!
 */
export const Newline = createToken({
  name: 'Newline',
  pattern: /\r?\n/,
  line_breaks: true
});

/**
 * Space is SIGNIFICANT in line content - it creates explicit spacing!
 * Multiple consecutive spaces are treated as one space element
 */
export const Space = createToken({
  name: 'Space',
  pattern: / +/  // One or more spaces
});

/**
 * Whitespace (tabs only) - skip these
 */
export const WhiteSpace = createToken({
  name: 'WhiteSpace',
  pattern: /\t+/,  // Only tabs
  group: Lexer.SKIPPED
});

export const Comment = createToken({
  name: 'Comment',
  pattern: /\/\/[^\n]*/,
  group: Lexer.SKIPPED
});

// ═══════════════════════════════════════════════════════════════════════════
// Component Keywords (must come before Identifier for correct matching)
// ═══════════════════════════════════════════════════════════════════════════

export const QUESTION_TEXT = createToken({
  name: 'QUESTION_TEXT',
  pattern: /QUESTION_TEXT/
});

export const TEXT_HIGHLIGHTER = createToken({
  name: 'TEXT_HIGHLIGHTER',
  pattern: /TEXT_HIGHLIGHTER/
});

export const COLOR_TEXT = createToken({
  name: 'COLOR_TEXT',
  pattern: /COLOR_TEXT/
});

export const BOLD_TEXT = createToken({
  name: 'BOLD_TEXT',
  pattern: /BOLD_TEXT/
});

export const HIGHLIGHT_TEXT = createToken({
  name: 'HIGHLIGHT_TEXT',
  pattern: /HIGHLIGHT_TEXT/
});

export const W_S_I = createToken({
  name: 'W_S_I',
  pattern: /W_S_I/
});

export const STRING_INPUT = createToken({
  name: 'STRING_INPUT',
  pattern: /STRING_INPUT/
});

export const INLINE_DRAG_OPTION = createToken({
  name: 'INLINE_DRAG_OPTION',
  pattern: /INLINE_DRAG_OPTION/
});

export const DRAGABLE_BUTTONS_OPTIONS = createToken({
  name: 'DRAGABLE_BUTTONS_OPTIONS',
  pattern: /DRAGABLE_BUTTONS_OPTIONS/
});

export const RADIO_BUTTONS_OPTIONS = createToken({
  name: 'RADIO_BUTTONS_OPTIONS',
  pattern: /RADIO_BUTTONS_OPTIONS/
});

export const NUMERIC_KEYPAD = createToken({
  name: 'NUMERIC_KEYPAD',
  pattern: /NUMERIC_KEYPAD/
});

export const WIDGET_ANSWER = createToken({
  name: 'WIDGET_ANSWER',
  pattern: /WIDGET_ANSWER/
});

export const W_N_I = createToken({
  name: 'W_N_I',
  pattern: /W_N_I/
});

export const NUMERIC_INPUT = createToken({
  name: 'NUMERIC_INPUT',
  pattern: /NUMERIC_INPUT/
});

export const NUMBER_LINE = createToken({
  name: 'NUMBER_LINE',
  pattern: /NUMBER_LINE/
});

export const POSITION = createToken({
  name: 'POSITION',
  pattern: /POSITION/
});

export const CURSOR = createToken({
  name: 'CURSOR',
  pattern: /CURSOR/
});
export const ARROW = createToken({
  name: 'ARROW',
  pattern: /ARROW/
});

export const TABLE = createToken({
  name: 'TABLE',
  pattern: /TABLE/
});

export const ROW = createToken({
  name: 'ROW',
  pattern: /ROW/
});

export const CELL = createToken({
  name: 'CELL',
  pattern: /CELL/
});

export const COUNTER = createToken({
  name: 'COUNTER',
  pattern: /COUNTER/
});

export const CLOCK = createToken({
  name: 'CLOCK',
  pattern: /CLOCK/
});

/**
 * HIGHLIGHT must use a negative lookahead to avoid shadowing HIGHLIGHT_TEXT.
 * Chevrotain uses longest-match, but being explicit avoids any edge-case
 * ambiguity in tooling (linters, debuggers).
 */
export const HIGHLIGHT = createToken({
  name: 'HIGHLIGHT',
  pattern: /HIGHLIGHT(?!_TEXT)/
});

export const VISUAL_FRACTION = createToken({
  name: 'VISUAL_FRACTION',
  pattern: /VISUAL_FRACTION/
});
export const FRACTION = createToken({
  name: 'FRACTION',
  pattern: /FRACTION/
});

export const PAIR_MATCHER = createToken({
  name: 'PAIR_MATCHER',
  pattern: /PAIR_MATCHER/
});
export const MATCHER = createToken({
  name: 'MATCHER',
  pattern: /MATCHER/
});


export const TOP_ROW = createToken({
  name: 'TOP_ROW',
  pattern: /TOP_ROW/
});

export const BOTTOM_ROW = createToken({
  name: 'BOTTOM_ROW',
  pattern: /BOTTOM_ROW/
});

export const ITEM = createToken({
  name: 'ITEM',
  pattern: /ITEM/
});

export const ANSWERS = createToken({
  name: 'ANSWERS',
  pattern: /ANSWERS/
});

export const IMAGE = createToken({
  name: 'IMAGE',
  pattern: /IMAGE/
});

export const VERTICAL_OPERATION = createToken({
  name: 'VERTICAL_OPERATION',
  pattern: /VERTICAL_OPERATION/
});

export const PROTRACTOR = createToken({
  name: 'PROTRACTOR',
  pattern: /PROTRACTOR/
});

export const SET_SQUARE = createToken({
  name: 'SET_SQUARE',
  pattern: /SET_SQUARE/
});

export const RULER = createToken({
  name: 'RULER',
  pattern: /RULER/
});

/**
 * Matches only inside a VERTICAL_OPERATION body. Lookahead allows `SEP`,
 * `SEP {}`, `SEP\n` while avoiding collision with a `SEP`-prefixed identifier.
 */
export const SEP = createToken({
  name: 'SEP',
  pattern: /SEP(?![a-zA-Z0-9_])/
});

export const CARRY = createToken({
  name: 'CARRY',
  pattern: /CARRY(?![a-zA-Z0-9_])/
});

/**
 * Matches only when immediately followed by `[` (widget form `INPUT[...]`).
 * Prevents shadowing the word "INPUT" when used as plain Identifier/FreeText.
 */
export const INPUT = createToken({
  name: 'INPUT',
  pattern: /INPUT(?=\s*\[)/
});

/**
 * Standalone underscore placeholder for empty cells in VERTICAL_OPERATION.
 * Lookahead prevents matching leading underscore of identifiers like `_foo`.
 */
export const Underscore = createToken({
  name: 'Underscore',
  pattern: /_(?![a-zA-Z0-9_])/
});

// ═══════════════════════════════════════════════════════════════════════════
// Literals
// ═══════════════════════════════════════════════════════════════════════════

export const NumberLiteral = createToken({
  name: 'NumberLiteral',
  pattern: /-?\d+(\.\d+)?/
});

export const StringLiteral = createToken({
  name: 'StringLiteral',
  pattern: /"[^"]*"/
});

// ═══════════════════════════════════════════════════════════════════════════
// Identifiers (for widget parameter names, variable references)
// ═══════════════════════════════════════════════════════════════════════════

export const Identifier = createToken({
  name: 'Identifier',
  pattern: /[a-zA-Z_][a-zA-Z0-9_]*/
});

// ═══════════════════════════════════════════════════════════════════════════
// Operators
// ═══════════════════════════════════════════════════════════════════════════

export const Plus = createToken({
  name: 'Plus',
  pattern: /\+/
});

export const Minus = createToken({
  name: 'Minus',
  pattern: /−|-/  // Unicode minus (U+2212) and ASCII hyphen-minus
});

export const Multiply = createToken({
  name: 'Multiply',
  pattern: /×|\*/  // Unicode multiplication sign and ASCII asterisk
});

export const Divide = createToken({
  name: 'Divide',
  pattern: /÷|\//  // Unicode division sign and ASCII forward slash
});

export const Equals = createToken({
  name: 'Equals',
  pattern: /=/
});

export const LessThan = createToken({
  name: 'LessThan',
  pattern: /</
});

export const GreaterThan = createToken({
  name: 'GreaterThan',
  pattern: />/
});

export const Question = createToken({
  name: 'Question',
  pattern: /\?/
});

// ═══════════════════════════════════════════════════════════════════════════
// Delimiters
// ═══════════════════════════════════════════════════════════════════════════

export const LParen = createToken({
  name: 'LParen',
  pattern: /\(/
});

export const RParen = createToken({
  name: 'RParen',
  pattern: /\)/
});

export const LBracket = createToken({
  name: 'LBracket',
  pattern: /\[/
});

export const RBracket = createToken({
  name: 'RBracket',
  pattern: /\]/
});

export const Comma = createToken({
  name: 'Comma',
  pattern: /,/
});

export const LBrace = createToken({
  name: 'LBrace',
  pattern: /\{/
});

export const RBrace = createToken({
  name: 'RBrace',
  pattern: /\}/
});

// ═══════════════════════════════════════════════════════════════════════════
// Free Text (Natural writing - no component wrapper needed!)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * FreeText captures any natural text/numbers that aren't special components.
 * This enables the "just type it" experience!
 *
 * Pattern: Match anything that's NOT a component start, newline, or special character
 * - NOT starting with uppercase component names followed by [
 * - NOT newlines (those are significant line separators)
 * - NOT spaces (they have their own token)
 * - NOT operators and delimiters (they have their own tokens)
 *
 * Must come LAST in token order to act as fallback!
 */
export const FreeText = createToken({
  name: 'FreeText',
  pattern: /(?:[^A-Z\n\r \t+\-−×*÷/=<>?()\[\],{}]|(?![A-Z_]+[\[{])[A-Z])+/,
  line_breaks: false
});

// ═══════════════════════════════════════════════════════════════════════════
// Token Order (CRITICAL for correct matching)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * All tokens in order of matching priority.
 *
 * CRITICAL ORDER RULES:
 * 1. Newline BEFORE Space and WhiteSpace
 * 2. Space BEFORE WhiteSpace (spaces are significant)
 * 3. Component keywords BEFORE Identifier
 * 4. FreeText LAST (acts as fallback for natural text)
 */
export const allTokens: TokenType[] = [
  // Newline (significant!) - FIRST
  Newline,

  // Space (significant!) - BEFORE WhiteSpace
  Space,

  // Skipped tokens
  WhiteSpace,
  Comment,

  // Component keywords (before Identifier!)
  QUESTION_TEXT,
  TEXT_HIGHLIGHTER,
  COLOR_TEXT,
  BOLD_TEXT,
  HIGHLIGHT_TEXT,
  W_S_I,
  STRING_INPUT,
  INLINE_DRAG_OPTION,
  DRAGABLE_BUTTONS_OPTIONS,
  RADIO_BUTTONS_OPTIONS,
  NUMERIC_KEYPAD,
  WIDGET_ANSWER,
  W_N_I,
  NUMERIC_INPUT,
  NUMBER_LINE,
  POSITION,
  CURSOR,
  ARROW,
  TABLE,
  ROW,
  TOP_ROW,
  BOTTOM_ROW,
  CELL,
  COUNTER,
  CLOCK,
  HIGHLIGHT,
  VISUAL_FRACTION,
  FRACTION,
  PAIR_MATCHER,
  MATCHER,
  ITEM,
  ANSWERS,
  IMAGE,
  VERTICAL_OPERATION,
  PROTRACTOR,
  SET_SQUARE,
  RULER,
  SEP,
  CARRY,
  INPUT,
  Underscore,

  // Literals
  NumberLiteral,
  StringLiteral,

  // Identifiers (after keywords)
  Identifier,

  // Operators
  Plus,
  Minus,
  Multiply,
  Divide,
  Equals,
  LessThan,
  GreaterThan,
  Question,

  // Delimiters
  LParen,
  RParen,
  LBracket,
  RBracket,
  Comma,
  LBrace,
  RBrace,

  // FreeText - LAST (fallback for natural text)
  FreeText
];
